// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/auth/clerkShim';
import { useOutletContext } from 'react-router-dom';
import { Plus, Save, Eye, FileText, Settings, Trash2, CheckCircle, Edit3, Monitor, Smartphone } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import useWaiverStore from '@/store/waiverStore';
import useOrganizationStore from '@/store/organizationStore';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import WaiverSigningPreview from './WaiverSigningPreview';
import { stripQuillNbsp } from '@/utils/quillHtml';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const WaiverAdmin = () => {
  const { getToken } = useAuth();
  const { leagueName = '' } = useOutletContext() as { leagueName?: string };
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [newWaiverName, setNewWaiverName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  // Desktop/mobile preview toggle, same pattern as Appearance (owner
  // 2026-09-12): desktop default; the toggle re-weights BOTH panes —
  // desktop preview needs the width (editor squishes to ~3/10), the
  // 390px mobile preview doesn't (editor takes the width back).
  const [mobilePreview, setMobilePreview] = useState(false);
  const quillRef = useRef(null);

  // Access state and actions from Zustand stores
  const { waivers, currentWaiver, isLoading, fetchWaiversByOrganization, createWaiver, updateWaiver, setCurrentWaiver } = useWaiverStore();
  const { organizations, getOrganizationByName } = useOrganizationStore();

  useEffect(() => {
    getOrganizationByName(leagueName);
  }, [leagueName]);

  useEffect(() => {
    if (organizations.length > 0) {
      const league = organizations.find(l => l.name === leagueName);
      if (league) {
        fetchWaiversByOrganization(league._id);
      }
    }
  }, [organizations, leagueName]);

  const handleCreateWaiver = async () => {
    if (!newWaiverName.trim()) return;

    const league = organizations.find(l => l.name === leagueName);
    if (!league) return;

    const waiverData = {
      name: newWaiverName.trim(),
      title: newWaiverName.trim(),
      html: '',
      organization: league._id
    };

    await createWaiver(getToken)(waiverData, (newWaiver) => {
      setCurrentWaiver(newWaiver);
      setNewWaiverName('');
      setIsCreateModalOpen(false);
    });
  };

  const handleWaiverSelect = (waiverId) => {
    const selectedWaiver = waivers.find(w => w._id === waiverId);
    setCurrentWaiver(selectedWaiver);
  };

  const handleTitleChange = (newTitle) => {
    if (currentWaiver) {
      setCurrentWaiver({
        ...currentWaiver,
        title: newTitle
      });
    }
  };

  // The editor is CONTROLLED, and react-quill-new is only "hybrid" controlled:
  // whenever the `value` prop differs from the string it last emitted, it
  // re-sets the editor contents, which makes Quill fire text-change again.
  // So the value fed back MUST be the untouched string from onChange. Quill 2's
  // getSemanticHTML() backs that string and turns every typed space into
  // &nbsp;; normalising it here (the pre-2026-10 code called stripQuillNbsp
  // in this handler) meant prop ≠ emitted on every keystroke: a trailing space
  // got collapsed away on the re-set (you could not type "hello world"), and a
  // mid-text space re-emitted the same &nbsp; string forever — zustand
  // setState inside ReactQuill's render → "Maximum update depth exceeded"
  // (Sentry ADMIN-7J, dash.staty.io/admin/waivers). The nbsp strip now
  // happens at the two READ boundaries instead: the save/finalize payload and
  // the live preview (see utils/quillHtml.ts for why it is needed at all).
  const handleContentChange = (html) => {
    if (currentWaiver) {
      setCurrentWaiver({
        ...currentWaiver,
        html
      });
    }
  };

  const handleSave = async () => {
    if (!currentWaiver) return;

    setIsSaving(true);
    try {
      // Save as draft (finalized: false)
      const draftWaiver = { ...currentWaiver, html: stripQuillNbsp(currentWaiver.html), finalized: false };
      await updateWaiver(getToken)(draftWaiver, (updatedWaiver) => {
        setCurrentWaiver(updatedWaiver);
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!currentWaiver) return;

    setIsFinalizing(true);
    try {
      // Finalize the waiver (finalized: true)
      const finalizedWaiver = { ...currentWaiver, html: stripQuillNbsp(currentWaiver.html), finalized: true };
      await updateWaiver(getToken)(finalizedWaiver, (updatedWaiver) => {
        setCurrentWaiver(updatedWaiver);
        setIsFinalizeModalOpen(false);
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  // Quill modules configuration
  const editorModules = {
    toolbar: {
      container: [
        [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
        [{size: ['small', 'medium', 'large', 'huge']}, { 'color': [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, 
        {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image', 'video'],
        ['clean']
      ],
    },
    clipboard: {
      matchVisual: false,
    }
  };

  // Quill 2 has no separate "bullet" format (bullets are `list: 'bullet'`);
  // listing it logged `Cannot register "bullet"` on every mount.
  const editorFormats = [
    'header', 'font', 'size', 'color',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'indent',
    'link', 'image', 'video'
  ];

  return (
    <PageContainer fullWidth className="h-screen flex flex-col space-y-0 gap-6 bg-muted/50">
      {/* Standard PageHeader in the page flow (2026-09-11) — the old bg-card
          top strip put the title at a different height than the other
          rail-mode pages (Email Templates / Settings); the in-page "Back to
          Dashboard" link is gone too (the AdminRail carries the back arrow). */}
      <PageHeader
        title={(
          <span className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-primary" />
            <span>Waiver Management</span>
            <span className="text-sm font-normal text-muted-foreground">• {leagueName}</span>
          </span>
        )}
        description="Write the waivers players agree to when they register. Drafts stay editable; finalizing locks the text so every signature refers to the exact version signed."
        actions={(
          <>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground justify-end">
              <Eye className="w-4 h-4" />
              <span>Preview</span>
            </div>
            {currentWaiver && (
              <div className="flex items-center space-x-2 text-sm justify-end">
                {currentWaiver.finalized ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-success" />
                    <span className="text-success font-medium">Finalized</span>
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4 text-warning" />
                    <span className="text-warning font-medium">Draft</span>
                  </>
                )}
              </div>
            )}
          </>
        )}
      />

      {/* Main Content Area — flex-1 min-h-0 (not h-full): the container is a
          flex column with the header above, so h-full would overflow. */}
      <div className="flex flex-1 min-h-0">
        {/* Left Sidebar */}
        <div className="w-72 bg-card border-r border-border flex-col">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground mb-2">Waiver Forms</h2>
            <p className="text-sm text-muted-foreground mb-4">Select a waiver to edit or create a new one</p>
            
            {/* Waiver Selection */}
            <div className="mb-4">
              <Label htmlFor="waiver-select" className="text-sm font-medium text-foreground">
                Select Waiver
              </Label>
              <Select onValueChange={handleWaiverSelect} value={currentWaiver?._id || ''}>
                <SelectTrigger className="w-full mt-1">
                  <SelectValue placeholder="Choose a waiver to edit" />
                </SelectTrigger>
                <SelectContent>
                  {waivers.map((waiver) => (
                    <SelectItem key={waiver._id} value={waiver._id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{waiver.name}</span>
                        <div className="flex items-center space-x-1">
                          {waiver.finalized ? (
                            <>
                              <CheckCircle className="w-3 h-3 text-success" />
                              <span className="text-xs text-success font-medium">Finalized</span>
                            </>
                          ) : (
                            <>
                              <Edit3 className="w-3 h-3 text-warning" />
                              <span className="text-xs text-warning font-medium">Draft</span>
                            </>
                          )}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Create New Waiver Button */}
            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Waiver
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Create New Waiver</DialogTitle>
                  <DialogDescription>
                    Enter a name for your new waiver form. You can edit the title and content after creation.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="waiver-name" className="text-right">
                      Name
                    </Label>
                    <Input
                      id="waiver-name"
                      value={newWaiverName}
                      onChange={(e) => setNewWaiverName(e.target.value)}
                      className="col-span-3"
                      placeholder="Enter waiver name"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    type="submit" 
                    onClick={handleCreateWaiver}
                    disabled={!newWaiverName.trim()}
                  >
                    Create Waiver
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Main Editor Area */}
        <div className="flex-1 flex flex-col">
          {currentWaiver ? (
            <>
              {/* Editor Toolbar */}
              <div className="bg-card border-b border-border px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-medium text-foreground">{currentWaiver.title}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {!currentWaiver.finalized ? (
                      <>
                        <Button
                          onClick={handleSave}
                          disabled={isSaving}
                          variant="outline"
                          className="px-4 py-2"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {isSaving ? 'Saving...' : 'Save Draft'}
                        </Button>
                        <Dialog open={isFinalizeModalOpen} onOpenChange={setIsFinalizeModalOpen}>
                          <DialogTrigger asChild>
                            <Button
                              disabled={isFinalizing || isSaving}
                              className="px-4 py-2 bg-success hover:bg-success/90"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Finalize Waiver
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[425px]">
                            <DialogHeader>
                              <DialogTitle>Finalize Waiver</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to finalize this waiver? Once finalized, it cannot be edited again.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button 
                                variant="outline" 
                                onClick={() => setIsFinalizeModalOpen(false)}
                                disabled={isFinalizing}
                              >
                                Cancel
                              </Button>
                              <Button 
                                onClick={handleFinalize}
                                disabled={isFinalizing}
                                className="bg-success hover:bg-success/90"
                              >
                                {isFinalizing ? 'Finalizing...' : 'Yes, Finalize'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </>
                    ) : (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-success" />
                        <span className="text-success font-medium">This waiver is finalized and cannot be edited</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Editor Content */}
              <div className="flex-1 flex min-h-0">
                {/* Editor Panel — squishes when the desktop preview needs
                    the width (Appearance's dynamic-squish behavior). */}
                <div className={`${mobilePreview ? 'flex-1' : 'flex-[3]'} min-w-0 bg-card transition-all duration-300`}>
                  <div className="h-full flex flex-col">
                    {/* Title Editor */}
                    <div className="p-4 border-b border-border">
                      <Label htmlFor="waiver-title" className="text-sm font-medium text-foreground mb-2 block">
                        Waiver Title
                      </Label>
                      <Input
                        id="waiver-title"
                        value={currentWaiver.title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        placeholder="Enter waiver title"
                        className="text-lg font-semibold"
                        disabled={currentWaiver.finalized}
                      />
                    </div>

                    {/* Content Editor */}
                    <div className="flex-1 p-4">
                      <Label className="text-sm font-medium text-foreground mb-2 block">
                        Waiver Content
                      </Label>
                      <div className="h-full">
                        {currentWaiver.finalized ? (
                          <div 
                            className="h-full p-4 border border-border rounded-md bg-muted/50 overflow-auto"
                            style={{ height: '400px' }}
                          >
                            <div 
                              className="prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: currentWaiver.html || '<p>No content available</p>' }}
                            />
                          </div>
                        ) : (
                          <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={currentWaiver.html}
                            onChange={handleContentChange}
                            modules={editorModules}
                            formats={editorFormats}
                            placeholder="Start writing your waiver content here..."
                            className="h-full"
                            style={{ height: '400px' }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview Panel */}
                <div className={`${mobilePreview ? 'w-[440px]' : 'flex-[7]'} bg-muted/50 border-l border-border flex flex-col transition-all duration-300`}>
                  <div className="p-4 border-b border-border bg-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">Live Preview</h3>
                        <p className="text-sm text-muted-foreground">See how your waiver will look</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant={mobilePreview ? 'ghost' : 'default'}
                          size="icon"
                          aria-label="Desktop preview"
                          aria-pressed={!mobilePreview}
                          onClick={() => setMobilePreview(false)}
                        >
                          <Monitor className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={mobilePreview ? 'default' : 'ghost'}
                          size="icon"
                          aria-label="Mobile preview"
                          aria-pressed={mobilePreview}
                          onClick={() => setMobilePreview(true)}
                        >
                          <Smartphone className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto p-4 md:p-6">
                    {/* The public signing card, replicated (owner 2026-09-12)
                        — see WaiverSigningPreview. Mobile mode narrows it to
                        the 390px phone column, like Appearance's preview. */}
                    <div className={mobilePreview ? 'mx-auto w-[390px] max-w-full' : 'mx-auto max-w-[820px]'}>
                      <WaiverSigningPreview
                        title={currentWaiver.title}
                        html={stripQuillNbsp(currentWaiver.html)}
                        league={organizations.find((l) => l.name === leagueName)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-card">
              <div className="text-center">
                <FileText className="w-16 h-16 text-muted-foreground/60 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Waiver Selected</h3>
                <p className="text-muted-foreground mb-4">
                  {waivers.length === 0 
                    ? "Create your first waiver form to get started"
                    : "Select a waiver from the sidebar to start editing"
                  }
                </p>
                {waivers.length === 0 && (
                  <Button onClick={() => setIsCreateModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Waiver
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default WaiverAdmin;
